/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import {
  enterpriseCompanyLogos,
  getEnterpriseCompanyLogosAltText,
} from 'bundles/unified-description-page-common/utils';

import EnterpriseLogosImage from 'images/unified-description-page/enterprise-logos.png';

import _t from 'i18n!nls/xdp';

type Props = {
  cta: React.ReactNode;
  title: string;
};

const styles = {
  title: css`
    max-width: 650px;
    position: relative;
    min-height: 80px;
  `,
  rectangle: css`
    min-height: 179px;
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-400) !important;
  `,
  cta: css`
    margin-top: 12px;
  `,
  imageWrapper: css`
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-300);
    }

    > div:first-child {
      width: 100%;
      height: auto;
      padding-bottom: unset;
      max-width: 400px;
    }
  `,
  enterpriseLogoImage: css`
    max-width: none !important;
    max-height: none !important;
    width: 100%;
    height: auto;
  `,
};

const ThinBannerModule = ({ cta, title }: Props) => {
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_upsell_card',
    {
      upsellType: 'enterprise',
      pageSection: {
        sectionName: 'thin_banner',
      },
      ...eventingProductData,
    }
  );

  return (
    <TrackedDiv
      trackingName="thin_banner_module"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Section ref={visibilityTrackingRef}>
        <PageGridContainer>
          <Grid item xs={12}>
            <div css={styles.rectangle}>
              <Grid container>
                <Grid item xs={12} md={8}>
                  <Typography css={styles.title} component="h1" variant="h1semibold">
                    {title}
                  </Typography>
                  {cta && <div css={styles.cta}>{cta}</div>}
                </Grid>
                <Grid item xs={12} md={4} css={styles.imageWrapper}>
                  <LazyImgix
                    src={EnterpriseLogosImage}
                    alt={_t(' logos of #{enterpriseCompanyLogos} ', {
                      enterpriseCompanyLogos: getEnterpriseCompanyLogosAltText(enterpriseCompanyLogos),
                    })}
                    maxWidth={320}
                    maxHeight={70}
                    css={styles.enterpriseLogoImage}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};

export default ThinBannerModule;
